import React, { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FileText,
  Car,
  BarChart2,
  Leaf,
  Download,
  ChevronDown,
  Bike,
  Truck,
  MoreHorizontal,
  Eye,
  MoreVertical,
  TreePine,
  Cloud,
} from "lucide-react";
import API_BASE_URL from "../api";

const API_BASE = `${API_BASE_URL}/api/inputHistory`;

// No endpoint yet for fuel-type/vehicle-category split — replace when available
const vehicleCategoryData = [
  { name: "Cars", value: 45, color: "#2E7D32" },
  { name: "Bikes", value: 30, color: "#F5A623" },
  { name: "Trucks", value: 15, color: "#1E6FD9" },
  { name: "Others", value: 10, color: "#9AA0A6" },
];

function StatCard({ label, value, unit, change, icon, iconBg, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col gap-2 shadow-sm">
      <div className="flex items-start justify-between">
        <span className="text-sm text-gray-500 flex items-center gap-1">
          {label}
          <span className="text-gray-300 text-xs">ⓘ</span>
        </span>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          {icon}
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        {loading ? (
          <span className="h-7 w-24 bg-gray-100 rounded animate-pulse" />
        ) : (
          <span className="text-2xl font-bold text-gray-900">{value}</span>
        )}
        {unit && !loading && <span className="text-sm text-gray-400">{unit}</span>}
      </div>
      {change && (
        <span className="text-xs font-medium text-emerald-600">
          ↑ {change} vs last {label.toLowerCase().includes("today") ? "yesterday" : "month"}
        </span>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const isVerified = status === "Verified";
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        isVerified ? "text-emerald-600" : "text-amber-500"
      }`}
    >
      <span className="text-[8px]">●</span>
      {status}
    </span>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-4 py-3">
        <p className="text-xs text-gray-400 mb-1">{label} {new Date().getFullYear()}</p>
        <p className="text-sm font-semibold text-emerald-700">
          {payload[0].value.toLocaleString()} Certificates
        </p>
      </div>
    );
  }
  return null;
}

export default function AdminOverview() {
  const [range] = useState("This Year");

  const [stats, setStats] = useState({
    totalCertificates: null,
    totalVehicles: null,
    totalCarbonFootprint: null,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(true);

  const [recentCertificates, setRecentCertificates] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const [certRes, vehicleRes, carbonRes] = await Promise.all([
          fetch(`${API_BASE}/totalCertificateGenerated`).then((r) => r.json()),
          fetch(`${API_BASE}/totalVehicleRegistered`).then((r) => r.json()),
          fetch(`${API_BASE}/totalCarbonFootprintGenerated`).then((r) => r.json()),
        ]);

        setStats({
          totalCertificates: certRes?.totalCertificate ?? 0,
          totalVehicles: vehicleRes?.totalVehicleRegistered ?? 0,
          totalCarbonFootprint: carbonRes?.totalCarbonFootprintGenerated ?? 0,
        });
      } catch (err) {
        console.error("Failed to load dashboard stats:", err);
        setStatsError("Couldn't load live stats. Showing last known values.");
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchTrend = async () => {
      setTrendLoading(true);
      try {
        const res = await fetch(`${API_BASE}/monthlyCertificateTrend`).then((r) => r.json());
        setTrendData(res?.trend || []);
      } catch (err) {
        console.error("Failed to load certificate trend:", err);
      } finally {
        setTrendLoading(false);
      }
    };

    const fetchRecent = async () => {
      setRecentLoading(true);
      try {
        const res = await fetch(`${API_BASE}/recentCertificates`).then((r) => r.json());
        setRecentCertificates(res?.recentCertificates || []);
      } catch (err) {
        console.error("Failed to load recent certificates:", err);
      } finally {
        setRecentLoading(false);
      }
    };

    fetchStats();
    fetchTrend();
    fetchRecent();
  }, []);

  const formattedCarbon =
    stats.totalCarbonFootprint !== null
      ? Number(stats.totalCarbonFootprint).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })
      : "—";

  return (
    <>
      {statsError && (
        <div className="mb-4 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
          {statsError}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-5 mb-6">
        <StatCard
          label="Total Green Certificates"
          value={stats.totalCertificates?.toLocaleString() ?? "—"}
          loading={statsLoading}
          iconBg="#E8F5E9"
          icon={<FileText size={18} className="text-emerald-700" />}
        />
        <StatCard
          label="Total CO₂ Saved"
          value={formattedCarbon}
          unit="Kg"
          loading={statsLoading}
          iconBg="#E3F1FE"
          icon={<Cloud size={18} className="text-blue-600" />}
        />
        <StatCard
          label="Total Vehicles Registered"
          value={stats.totalVehicles?.toLocaleString() ?? "—"}
          loading={statsLoading}
          iconBg="#F3E8FD"
          icon={<Car size={18} className="text-purple-600" />}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-12 gap-5 mb-6">
        {/* Trend chart */}
        <div className="col-span-5 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-1">
              Certificates Trend <span className="text-gray-300 text-xs">ⓘ</span>
            </h3>
            <button className="flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
              {range} <ChevronDown size={12} />
            </button>
          </div>
          {trendLoading ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
              Loading trend…
            </div>
          ) : trendData.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
              No certificate data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2E7D32" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2E7D32" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#9AA0A6" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${v / 1000}K` : v)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="certificates"
                  stroke="#2E7D32"
                  strokeWidth={2.5}
                  fill="url(#trendFill)"
                  dot={{ r: 3, fill: "#2E7D32", strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Vehicle category donut */}
        <div className="col-span-4 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Vehicle Category</h3>
            <span className="text-xs text-gray-400">Total Certificates</span>
          </div>
          <div className="relative flex items-center justify-center h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={vehicleCategoryData}
                  dataKey="value"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke="none"
                >
                  {vehicleCategoryData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute flex flex-col items-center">
              <span className="text-xl font-bold text-gray-900">
                {stats.totalVehicles?.toLocaleString() ?? "—"}
              </span>
              <span className="text-xs text-gray-400">Total</span>
            </div>
          </div>
          <div className="space-y-2.5 mt-3">
            {[
              { icon: Car, label: "Cars", value: "45%", color: "#1E6FD9" },
              { icon: Bike, label: "Bikes", value: "30%", color: "#F5A623" },
              { icon: Truck, label: "Trucks", value: "15%", color: "#2E7D32" },
              { icon: MoreHorizontal, label: "Others", value: "10%", color: "#9AA0A6" },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-gray-600">
                  <row.icon size={15} style={{ color: row.color }} />
                  {row.label}
                </span>
                <span className="font-semibold text-gray-800">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Carbon footprint impact */}
        <div className="col-span-3 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Carbon Footprint Impact</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 rounded-xl p-3">
              <Cloud size={16} className="text-emerald-700 mb-2" />
              <p className="text-sm font-bold text-gray-900">
                {statsLoading ? "…" : `${formattedCarbon} Kg`}
              </p>
              <p className="text-xs text-gray-500">Total CO₂ Saved</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3">
              <TreePine size={16} className="text-emerald-700 mb-2" />
              <p className="text-sm font-bold text-gray-900">
                {statsLoading ? "…" : stats.totalVehicles?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">Total Vehicles Registered</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3">
              <Leaf size={16} className="text-emerald-700 mb-2" />
              <p className="text-sm font-bold text-gray-900">
                {statsLoading || !stats.totalCertificates
                  ? "…"
                  : (
                      stats.totalCarbonFootprint / stats.totalCertificates
                    ).toFixed(2)}{" "}
                Kg
              </p>
              <p className="text-xs text-gray-500">Avg CO₂ Saved per Certificate</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3">
              <BarChart2 size={16} className="text-emerald-700 mb-2" />
              <p className="text-sm font-bold text-gray-900">
                {statsLoading ? "…" : stats.totalCertificates?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">Total Certificates Generated</p>
            </div>
          </div>
        </div>
      </div>

      {/* Banner */}
      <div className="bg-emerald-50 rounded-2xl px-6 py-5 mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 max-w-md">
          <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shrink-0">
            <Leaf size={26} className="text-emerald-700" />
          </div>
          <div>
            <p className="font-semibold text-emerald-800">Saving Today, Securing Tomorrow</p>
            <p className="text-sm text-gray-500">
              Your contribution is helping to reduce carbon emissions and build a greener future for everyone.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-8">
          {[
            { icon: Car, value: "578,403", label: "Car Certificates", color: "#2E7D32" },
            { icon: Bike, value: "385,602", label: "Bike Certificates", color: "#2E7D32" },
            { icon: Truck, value: "192,801", label: "Truck Certificates", color: "#F5A623" },
            { icon: MoreHorizontal, value: "128,534", label: "Other Certificates", color: "#9AA0A6" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${item.color}1A` }}
              >
                <item.icon size={16} style={{ color: item.color }} />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent certificates table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Recent Green Certificates</h3>
          <button className="text-xs font-medium border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
            View All
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs">
                <th className="px-6 py-3 font-medium">Certificate No.</th>
                <th className="px-4 py-3 font-medium">Vehicle No.</th>
                <th className="px-4 py-3 font-medium">Fuel Type</th>
                <th className="px-4 py-3 font-medium">Owner Name</th>
                <th className="px-4 py-3 font-medium">CO₂ Emission (kg)</th>
                <th className="px-4 py-3 font-medium">Distance (km)</th>
                <th className="px-4 py-3 font-medium">Issue Date</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {recentLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-400">
                    Loading recent certificates…
                  </td>
                </tr>
              ) : recentCertificates.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-400">
                    No certificates found
                  </td>
                </tr>
              ) : (
                recentCertificates.map((row, i) => (
                  <tr
                    key={row.certificateNumber || i}
                    className={i !== recentCertificates.length - 1 ? "border-b border-gray-50" : ""}
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {row.certificateNumber || "—"}
                    </td>
                    <td className="px-4 py-4 text-gray-600">{row.vehicleNumber}</td>
                    <td className="px-4 py-4 text-gray-700">{row.fuelType || "—"}</td>
                    <td className="px-4 py-4 text-gray-700">{row.ownerName}</td>
                    <td className="px-4 py-4 text-gray-700">
                      {typeof row.carbonFootprint === "number"
                        ? row.carbonFootprint.toFixed(2)
                        : "—"}
                    </td>
                    <td className="px-4 py-4 text-gray-700">{row.distance ?? "—"}</td>
                    <td className="px-4 py-4 text-gray-500">
                      {row.issueDate
                        ? new Date(row.issueDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-4 py-4">
                      <StatusPill status={row.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-3 text-gray-400">
                        {row.pdfUrl && (
                          <>
                            <a
                              href={row.pdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              title="View certificate"
                            >
                              <Eye size={16} className="cursor-pointer hover:text-gray-600" />
                            </a>
                            <a href={row.pdfUrl} download title="Download certificate">
                              <Download size={16} className="cursor-pointer hover:text-gray-600" />
                            </a>
                          </>
                        )}
                        <MoreVertical size={16} className="cursor-pointer hover:text-gray-600" />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}